import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { setItem } from '@propertypal/shared/src/services/local.storage';
import { Agent, Reason } from '@propertypal/shared/src/types/agents';
import { WebsiteUser } from '@propertypal/shared/src/types/websiteUser';
import { validPrefix } from '@propertypal/shared/src/utils/phoneNumber';
import platform from '@propertypal/shared/src/utils/platform';
import { validEmail, validPhoneCharacters } from '@propertypal/shared/src/utils/regex';
import Button from '@propertypal/web-ui/src/buttons/Button';
import Checkbox from '@propertypal/web-ui/src/inputs/Checkbox';
import PhoneNumberInput from '@propertypal/web-ui/src/inputs/PhoneNumberInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import TextArea from '@propertypal/web-ui/src/inputs/TextArea';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import Recaptcha, { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { useFormik } from 'formik';
import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import useCachedUser, { PROFILE_STORAGE_KEY } from '../../hooks/useCachedUser';
import { InputRow } from './AgentEmailForm.style';

interface Props {
  agents: Agent[];
  reasons: Reason[];
  onSubmit?: () => void;
  compact?: boolean;
  loggedInUser?: WebsiteUser;
  contactAll?: boolean;
}

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .matches(validEmail, 'Please enter a valid email address')
    .required('Please enter your email address'),
  prefix: yup.string().trim().test(validPrefix).required('Please enter a country code'),
  telephone: yup
    .string()
    .trim()
    .matches(validPhoneCharacters, 'Please enter a valid phone number')
    .required('Please enter your phone number'),
  name: yup.string().trim().required('Please enter your name'),
  comments: yup.string().trim().required('Please enter some comments'),
});

const AgentEmailForm: FunctionComponent<Props> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const reasonOptions = props.reasons.map((reason) => ({
    label: reason.text,
    value: String(reason.id),
  }));
  const [branch] = props.agents;

  const accountOptions = props.agents
    .filter((agent) => agent.emailEnabled)
    .map((agent) => ({
      label: `${agent.organisation} (${agent.organisationBranch})`,
      value: agent.accountNumber,
    }));

  const formik = useFormik({
    initialValues: {
      accountNumber: props.agents[0].accountNumber,
      reason: '1',
      name: '',
      email: '',
      prefix: '',
      telephone: '',
      comments: '',
      rememberMe: false,
    },
    validationSchema,
    onSubmit: async (values, formikHelpers) => {
      if (values.rememberMe) {
        setItem(PROFILE_STORAGE_KEY, {
          name: values.name,
          email: values.email,
          phoneNumber: `${values.prefix}${values.telephone}`,
        });
      }

      const { accountNumber, ...data } = values;

      setLoading(true);

      try {
        const recaptcha = await getCaptchaKey('enquire');
        const contacts = props.contactAll ? [] : [accountNumber];
        const promises: Promise<any>[] = [];
        const body = {
          ...data,
          reason: parseInt(values.reason, 10),
          recaptcha,
        };

        contacts.forEach((contact) => {
          promises.push(
            axios(
              {
                method: 'POST',
                url: `${API_URL}/account/${contact}/enquiry-with-recaptcha`,
                data: body,
              },
              dispatch,
            ),
          );
        });

        await Promise.all(promises);

        if (props.onSubmit) props.onSubmit();

        formikHelpers.resetForm();

        openToast({
          type: 'success',
          message: 'Your message has been sent!',
        });
      } catch (err: any) {
        openToast({
          type: 'error',
          message: 'Something went wrong. Please try again later',
        });
      } finally {
        setLoading(false);
      }
    },
  });

  useCachedUser(props.loggedInUser, (formDetails) => {
    formik.setValues({
      ...formik.values,
      name: formDetails.name,
      email: formDetails.email,
      prefix: formDetails.prefix,
      telephone: formDetails.phoneNumberNoPrefix,
      rememberMe: true,
    });
  });

  return (
    <>
      <Recaptcha />

      <form onSubmit={formik.handleSubmit}>
        {!props.contactAll && props.agents.length > 1 && (
          <SelectInput
            ariaLabel="Select a branch"
            label="Select a branch"
            id="accountNumber"
            options={accountOptions}
            value={formik.values.accountNumber}
            onChange={formik.handleChange('accountNumber')}
          />
        )}

        <InputRow compact={props.compact}>
          {!branch.developer && (
            <SelectInput
              ariaLabel="Reason for your enquiry"
              label="Reason for your enquiry"
              id="enquiry-reason"
              options={reasonOptions}
              value={formik.values.reason}
              onChange={formik.handleChange('reason')}
              testID="enquiry-reason"
            />
          )}

          <TextInput
            onValueChange={formik.handleChange('name')}
            value={formik.values.name}
            error={formik.touched.name && formik.errors.name}
            name="name"
            label="Name"
            placeholder="Name"
          />
        </InputRow>

        <InputRow compact={props.compact}>
          <TextInput
            onValueChange={formik.handleChange('email')}
            value={formik.values.email}
            error={formik.touched.email && formik.errors.email}
            name="email"
            label="Email"
            placeholder="Email"
            type="email"
          />

          <PhoneNumberInput
            value={formik.values.telephone}
            onValueChange={formik.handleChange('telephone')}
            prefix={formik.values.prefix}
            onPrefixChange={formik.handleChange('prefix')}
            error={formik.touched.telephone && (formik.errors.telephone || formik.errors.prefix)}
            label="Phone"
            placeholder="Phone"
          />
        </InputRow>

        <TextArea
          placeholder="Your message"
          containerStyle={{ marginTop: 20 }}
          name="comments"
          value={formik.values.comments}
          onValueChange={formik.handleChange('comments')}
          rows={3}
          error={formik.touched.comments && formik.errors.comments}
        />

        <Checkbox
          containerStyle={{ marginTop: 15 }}
          name="rememberMe"
          label="Remember my details"
          value={formik.values.rememberMe}
          onValueChange={(value) => formik.setFieldValue('rememberMe', value)}
        />

        <Button containerStyle={{ marginTop: 30 }} loading={loading} type="submit" testID="submitAgentEnquiry">
          {platform.select('Email the Agent', 'Message Agent')}
        </Button>
      </form>
    </>
  );
};

export default AgentEmailForm;
